import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ApiTypes, AiLlmTypes, HttpError, WorkspaceTypes } from '@flashbacktech/flashbackclient/api';
import { useApi } from '../contexts/ApiContext';
import { useAuth, useWorkspaces, useSelectedWorkspace } from '../contexts/AuthContext';
import { useWithTokenRefresh } from '../utils/api';
import { toastRepoCreated, toastErrorCustom } from '../utils/toast';

const AddRepo: React.FC = () => {
  const navigate = useNavigate();
  const { repoId } = useParams<{ repoId?: string }>();
  const { apiClient } = useApi();
  const { isAuthenticated, authState, setSelectedWorkspace } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();

  const isEditMode = !!repoId;
  const [availableUnits, setAvailableUnits] = useState<ApiTypes.StorageUnit[]>([]);
  const [availableAiLlms, setAvailableAiLlms] = useState<AiLlmTypes.AiLlmDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingRepo, setLoadingRepo] = useState<boolean>(false);
  
  // Use centralized workspace hooks
  const workspaces = useWorkspaces();
  const globalSelectedWorkspaceId = useSelectedWorkspace();
  
  // Local workspace selection for this form (can differ from global in edit mode)
  const [localWorkspaceId, setLocalWorkspaceId] = useState<string>('');

  const [newRepo, setNewRepo] = useState<ApiTypes.CreateRepoWithBucketsRequest>({
    name: '',
    storageType: ApiTypes.StorageType.S3,
    mode: ApiTypes.ModeType.NORMAL,
    repoBuckets: [],
    repoAiLlms: [],
    workspaceId: '',
  });

  // API key creation removed from the Add Repo flow

  const [validationErrors, setValidationErrors] = useState<{ name?: string }>({});
  // Wizard step gating: 0 = Details, 1 = Buckets, 2 = API Keys
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const isStepValid = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return !!newRepo.name?.trim();
      case 1:
        // Buckets and AI LLMs are optional - user can proceed without linking any
        return true;
      case 2:
        // API Keys optional
        return true;
      default:
        return false;
    }
  };

  // Get the effective workspace ID to use for API calls
  const getWorkspaceIdToUse = () => {
    // In edit mode, use local workspace; otherwise use global
    const workspaceIdToUse = localWorkspaceId || globalSelectedWorkspaceId;
    if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
      throw new Error('Please select a workspace');
    }
    return workspaceIdToUse;
  };
  
  // Effective selected workspace ID (local takes precedence)
  const selectedWorkspaceId = localWorkspaceId || globalSelectedWorkspaceId;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const loadUnits = async () => {
      try {
        // CRITICAL: Pass workspaceId to API call
        // Use local workspace or global, but only if not 'ALL'
        const workspaceIdToUse = localWorkspaceId || globalSelectedWorkspaceId;
        const workspaceIdParam = workspaceIdToUse && workspaceIdToUse !== 'ALL' ? workspaceIdToUse : undefined;
        const res = await withTokenRefresh(() => apiClient.getStorageBuckets(workspaceIdParam));
        setAvailableUnits(res?.buckets || []);
      } catch (error) { /* Ignore errors */ }
    };

    const loadAiLlms = async () => {
      try {
        const res = await withTokenRefresh(() => apiClient.getAvailableAiLlms());
        setAvailableAiLlms(res?.aiLlms || []);
      } catch (error) { /* Ignore errors */ }
    };

    // Workspaces are now loaded from AuthContext via useWorkspaces() hook
    // Initialize local workspace from global selection
    const initLocalWorkspace = () => {
      if (!isEditMode) {
        // In create mode, use global workspace or first available
        if (globalSelectedWorkspaceId && globalSelectedWorkspaceId !== 'ALL') {
          setLocalWorkspaceId(globalSelectedWorkspaceId);
        } else if (workspaces.length > 0) {
          setLocalWorkspaceId(workspaces[0].id);
        }
      }
    };
    initLocalWorkspace();

    const loadRepoForEdit = async () => {
      if (!repoId) return;

      setLoadingRepo(true);
      try {
        const repoResponse = await withTokenRefresh(() => apiClient.getStorageRepo(repoId));
        if (repoResponse.success && repoResponse.repo) {
          const repo = repoResponse.repo;
          // Pré-remplir le formulaire avec les données du repo
          setNewRepo({
            name: repo.name,
            storageType: repo.storageType,
            mode: repo.mode,
            workspaceId: repo.workspaceId,
            repoBuckets: (repo.buckets || []).map((bucket: ApiTypes.RepoBucketInfo) => ({
              bucketId: bucket.bucketId || '',
              folder: bucket.folder || '',
              master: bucket.master || false,
            })),
            repoAiLlms: (repo.aiLlms || []).map((aiLlm: AiLlmTypes.RepoAiLlmInfo) => ({
              aiLlmId: aiLlm.aiLlmId || '',
            })),
          });

          // En mode édition, commencer à l'étape 0 pour permettre la modification du nom
          setCurrentStep(0);

          // Extraire le workspaceId si disponible
          const wid = repo.workspaceId;
          setLocalWorkspaceId(wid || '');
        } else {
          toastErrorCustom('Repository not found');
          navigate('/repositories/inventory');
        }
      } catch (error) {
        toastErrorCustom('Failed to load repository');
        navigate('/repositories/inventory');
      } finally {
        setLoadingRepo(false);
      }
    };

    loadUnits();
    loadAiLlms();

    // Si en mode édition, charger les données du repo
    if (isEditMode) {
      loadRepoForEdit();
    }
  }, [
    apiClient,
    isAuthenticated,
    navigate,
    withTokenRefresh,
    authState?.token,
    repoId,
    isEditMode,
  ]);

  // When workspace changes, ensure selected repoUnits are consistent with workspace filter
  useEffect(() => {
    if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') return;
    setNewRepo((prev) => {
      const filtered = (prev.repoBuckets || []).filter((ru) => {
        const bucket = availableUnits.find((u) => u.id === ru.bucketId);
        return bucket?.workspaceId === selectedWorkspaceId;
      });
      if (filtered.length === (prev.repoBuckets || []).length) return prev;
      return { ...prev, repoBuckets: filtered };
    });
  }, [selectedWorkspaceId, availableUnits]);

  const validateForm = () => {
    const errors: typeof validationErrors = {};
    if (!newRepo.name?.trim()) errors.name = 'Name is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateRepo = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      // Determine workspace to use: if global selection is ALL, use local selected; else use global
      const workspaceIdToUse = getWorkspaceIdToUse();

      // Include workspaceId in creation request
      const createStorageRepo: ApiTypes.CreateRepoWithBucketsRequest = {
        name: newRepo.name || '',
        storageType: newRepo.storageType || ApiTypes.StorageType.S3,
        mode: newRepo.mode || ApiTypes.ModeType.NORMAL,
        workspaceId: workspaceIdToUse,
        repoBuckets: (newRepo.repoBuckets || []).map((u) => ({
          bucketId: u.bucketId || '',
          folder: u.folder || '',
          master: !!u.master,
        })),
        repoAiLlms: (newRepo.repoAiLlms || []).map((a) => ({
          aiLlmId: a.aiLlmId || '',
        })),
      };

      const response = await withTokenRefresh(() => apiClient.createStorageRepo(createStorageRepo));

      if (!response?.success || !response?.repoId) {
        throw new Error('Unexpected API response while creating repo');
      }

      const createdRepoId = response.repoId;

      toastRepoCreated();
      // Notify that repo was created (for tutorial)
      window.dispatchEvent(new CustomEvent('repoCreated'));
      // Redirect directly to the new repo details and scroll to API Keys section
      navigate(`/repositories/inventory/${createdRepoId}?section=api-keys`);
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 400:
            toastErrorCustom('Repo name must be unique');
            break;
          default:
            toastErrorCustom(`Failed to create storage repo: ${error.message}`);
        }
      } else {
        toastErrorCustom('Failed to create storage repo. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRepo = async () => {
    if (!validateForm() || !repoId) return;
    setLoading(true);

    try {
      // Use getWorkspaceIdToUse which handles local/global workspace selection
      const workspaceIdToUse = getWorkspaceIdToUse();

      const updateStorageRepo: ApiTypes.UpdateRepoRequest = {
        name: newRepo.name || '',
        storageType: newRepo.storageType || ApiTypes.StorageType.S3,
        mode: newRepo.mode || ApiTypes.ModeType.NORMAL,
        workspaceId: workspaceIdToUse,
        repoUnits: (newRepo.repoBuckets || []).map((u) => ({
          bucketId: u.bucketId || '',
          folder: u.folder || '',
          master: !!u.master,
        })),
        repoAiLlms: (newRepo.repoAiLlms || []).map((a) => ({
          aiLlmId: a.aiLlmId || '',
        })),
      };

      const response = await withTokenRefresh(() =>
        apiClient.updateStorageRepo(repoId, updateStorageRepo)
      );

      if (response?.success) {
        toastRepoCreated();
        navigate(`/repositories/inventory/${repoId}`);
      } else {
        throw new Error('Unexpected API response while updating repo');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastErrorCustom('Repository not found');
            break;
          case 400:
            toastErrorCustom('Repo name must be unique');
            break;
          default:
            toastErrorCustom(`Failed to update storage repo: ${error.message}`);
        }
      } else {
        toastErrorCustom('Failed to update storage repo. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const addRepoBucket = (unit: ApiTypes.StorageUnit) => {
    setNewRepo((prev) => ({
      ...prev,
      repoBuckets: [...(prev.repoBuckets || []), { bucketId: unit.id, folder: '', master: false }],
    }));
  };

  const removeRepoUnit = (index: number) => {
    setNewRepo((prev) => ({
      ...prev,
      repoBuckets: (prev.repoBuckets || []).filter((_, i) => i !== index),
    }));
  };

  if (loadingRepo) {
    return (
      <Box
        className="w-full"
        sx={{
          px: 5,
          py: 6,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Typography variant="h6" color="textSecondary">
          Loading repository...
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="w-full" sx={{ px: 5, py: 6 }}>
      <Box className="flex items-center justify-between mb-6">
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '20px',
              color: '#181D27',
              lineHeight: '30px',
            }}
          >
            {isEditMode ? 'Edit repository' : 'Add repository'}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontWeight: 400,
              fontSize: '14px',
              color: '#535862',
            }}
          >
            {isEditMode
              ? 'Update your repository details below.'
              : 'Follow the simple steps below to create a repository.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => setShowHelp((v) => !v)}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              width: 36,
              height: 36,
            }}
            aria-label="Help and Support"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <Button
            onClick={() => navigate('/repositories/inventory')}
            sx={{
              width: '80px',
              height: '36px',
              pt: '10px',
              pr: '14px',
              pb: '10px',
              pl: '14px',
              gap: '4px',
              borderRadius: '8px',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: '#E9EAEB',
              backgroundColor: '#FFFFFF',
              color: '#181D27',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#F9FAFB' },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {/* Divider under header */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
        <Divider sx={{ width: '1080px', borderColor: '#E5E7EB' }} />
      </Box>

      {/* Section: Repository details */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            color: '#181D27',
            lineHeight: '26px',
            mb: 1,
          }}
        >
          Repository details
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            color: '#535862',
            lineHeight: '20px',
            mb: 3,
          }}
        >
          Give your repo a name
        </Typography>

        {/* Help and Support Panel */}
        {showHelp && (
          <Box
            sx={{
              width: '100%',
              maxWidth: 350,
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              mb: 3,
            }}
          >
            <Box
              sx={{
                height: 70,
                px: 2,
                background: '#FAFAFA',
                borderBottom: '1px solid #E5E7EB',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Help and Support</Typography>
              <IconButton size="small" onClick={() => setShowHelp(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
              {/* What's a Repository in Flashback? */}
              <Box
                component="a"
                href="https://docs.flashback.tech/flashback-platform/cloud-storage/repositories"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  background: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#617BFF',
                    background: '#F9FAFB',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    background: '#F0F2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M13 5h4m0 0v4m0-4L9 13"
                      stroke="#617BFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#181D27' }}>
                    What's a Repository in Flashback?
                  </Typography>
                  <Typography sx={{ fontWeight: 400, fontSize: 13, lineHeight: '18px', color: '#717680', mt: 0.25 }}>
                    Learn about repository concepts
                  </Typography>
                </Box>
              </Box>

              {/* Create a Repository */}
              <Box
                component="a"
                href="https://docs.flashback.tech/guides/create-a-repository"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  background: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#617BFF',
                    background: '#F9FAFB',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    background: '#F0F2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 5v10m-5-5h10"
                      stroke="#617BFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#181D27' }}>
                    Create a Repository
                  </Typography>
                  <Typography sx={{ fontWeight: 400, fontSize: 13, lineHeight: '18px', color: '#717680', mt: 0.25 }}>
                    Follow the step-by-step guide
                  </Typography>
                </Box>
              </Box>

              {/* Build with API */}
              <Box
                component="a"
                href="https://docs.flashback.tech/support-reference/storage-api-operations"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  background: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#617BFF',
                    background: '#F9FAFB',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    background: '#F0F2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M8 8h8M8 12h8m-8 4h8M4 8h.01M4 12h.01M4 16h.01"
                      stroke="#617BFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#181D27' }}>
                    Build with API
                  </Typography>
                  <Typography sx={{ fontWeight: 400, fontSize: 13, lineHeight: '18px', color: '#717680', mt: 0.25 }}>
                    Explore the Repository API
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Workspace selection or badge */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#181D27',
              mb: 1,
            }}
          >
            Workspace *
          </Typography>
          {/* In edit mode or when workspace is fixed, show badge */}
          {isEditMode || globalSelectedWorkspaceId !== 'ALL' ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                border: '1px solid #E9EAEB',
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                color: '#414651',
                fontSize: '12px',
                fontWeight: 600,
              }}
              title={
                isEditMode
                  ? 'Workspace cannot be changed when editing'
                  : 'Workspace selection is controlled by the global selector in the sidebar'
              }
            >
              {(() => {
                const wsId = selectedWorkspaceId || globalSelectedWorkspaceId;
                const w = workspaces.find((ws) => ws.id === wsId);
                return w ? w.name : wsId;
              })()}
            </Box>
          ) : (
            <FormControl
              sx={{
                width: '100%',
                maxWidth: '712px',
                '& .MuiOutlinedInput-root': {
                  height: '44px',
                  borderRadius: '8px',
                  '& fieldset': { borderWidth: '1px', borderColor: '#E9EAEB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                },
                '& .MuiSelect-select': { padding: '10px 14px', fontSize: '14px' },
              }}
              size="small"
            >
              <Select
                value={selectedWorkspaceId}
                displayEmpty
                onChange={(e) => {
                  const val = e.target.value as string;
                  setLocalWorkspaceId(val);
                  // Also update global selection
                  setSelectedWorkspace(val);
                }}
                renderValue={(value) => {
                  if (!value) return <span style={{ color: '#9CA3AF' }}>Select a workspace</span>;
                  const w = workspaces.find((ws) => ws.id === value);
                  return w ? w.name : value;
                }}
              >
                {workspaces.length === 0 && (
                  <MenuItem value="" disabled>
                    No accessible workspace
                  </MenuItem>
                )}
                {workspaces.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#181D27',
              mb: 1,
            }}
          >
            Name *
          </Typography>
          <TextField
            placeholder="Choose something unique"
            value={newRepo.name}
            onChange={(e) => setNewRepo((p) => ({ ...p, name: e.target.value }))}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            required
            data-tutorial="repo-name"
            sx={{
              width: '100%',
              maxWidth: '712px',
              '& .MuiOutlinedInput-root': {
                height: '44px',
                borderRadius: '8px',
                '& fieldset': {
                  borderWidth: '1px',
                  borderColor: '#E9EAEB',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#617BFF',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          />
        </Box>


        {/* Hide Next button in edit mode */}
        {!isEditMode && (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              onClick={() => {
                if (!isStepValid(0)) {
                  validateForm();
                  return;
                }
                setCurrentStep(1);
              }}
              disabled={!isStepValid(0)}
              sx={{
                backgroundColor: '#617BFF',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#5169E0',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#9CA3AF',
                },
              }}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: '20px' }}
              >
                <span style={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>Next</span>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 12h12"
                    stroke="#BDC8FF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6l6 6-6 6"
                    stroke="#BDC8FF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Button>
          </Box>
        )}
      </Box>

      {/* Divider between sections */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
        <Divider sx={{ width: '1080px', borderColor: '#E5E7EB' }} />
      </Box>

      {/* Section: Link Buckets */}
      <Box
        sx={{
          mb: 4,
          position: 'relative',
          ...(!isEditMode && currentStep < 1
            ? { opacity: 0.5, pointerEvents: 'none' as const }
            : {}),
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            color: '#181D27',
            lineHeight: '26px',
            mb: 1,
          }}
        >
          Link Buckets
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            color: '#535862',
            lineHeight: '20px',
            mb: 3,
          }}
        >
          Select and link buckets
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: '#181D27',
              }}
            >
              Storage type
            </Typography>
            <Box
              component="span"
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'help',
                '&:hover .tooltip-content': {
                  opacity: 1,
                  visibility: 'visible',
                },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: '#667085' }} />
              <Box
                className="tooltip-content"
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  mb: 1,
                  px: 2,
                  py: 1.5,
                  backgroundColor: '#1F2937',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  fontSize: '12px',
                  lineHeight: '18px',
                  whiteSpace: 'nowrap',
                  opacity: 0,
                  visibility: 'hidden',
                  transition: 'opacity 0.2s, visibility 0.2s',
                  zIndex: 1000,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              >
                <strong>S3:</strong> Compatible with AWS S3 SDK<br />
                <strong>GCS:</strong> Compatible with Google Cloud Storage SDK<br />
                <strong>Azure:</strong> Compatible with Azure Blob Storage SDK
              </Box>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '12px',
              color: '#F59E0B',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            ⚠️ This choice is permanent and cannot be changed after creation.
          </Typography>
          <FormControl
            sx={{
              width: '100%',
              maxWidth: '712px',
              mb: 3,
              '& .MuiOutlinedInput-root': {
                height: '44px',
                borderRadius: '8px',
                '& fieldset': {
                  borderWidth: '1px',
                  borderColor: '#E9EAEB',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#617BFF',
                },
              },
              '& .MuiSelect-select': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          >
            <Select
              value={newRepo.storageType ?? ApiTypes.StorageType.S3}
              onChange={(e) =>
                setNewRepo((p) => ({ ...p, storageType: e.target.value as ApiTypes.StorageType }))
              }
              displayEmpty
              renderValue={(value) => {
                if (!value) return <span style={{ color: '#9CA3AF' }}>Select an option</span>;
                return value === ApiTypes.StorageType.S3
                  ? 'S3'
                  : value === ApiTypes.StorageType.GCS
                  ? 'GCS'
                  : 'Azure';
              }}
            >
              <MenuItem value={ApiTypes.StorageType.S3}>S3</MenuItem>
              <MenuItem value={ApiTypes.StorageType.GCS}>GCS</MenuItem>
              <MenuItem value={ApiTypes.StorageType.BLOB}>Azure</MenuItem>
            </Select>
          </FormControl>

        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#181D27',
              mb: 1,
            }}
          >
            Select buckets
          </Typography>
          <Autocomplete
            multiple
            options={availableUnits.filter((opt: ApiTypes.StorageUnit) => {
              if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') return true;
              const wid =
                opt.workspaceId;
              return wid === selectedWorkspaceId;
            })}
            getOptionLabel={(opt: ApiTypes.StorageUnit) => `${opt.name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Start typing..."
                sx={{
                  width: '712px',
                  '& .MuiOutlinedInput-root': {
                    minHeight: '44px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#E9EAEB',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D1D5DB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#617BFF',
                    },
                  },
                  '& .MuiAutocomplete-input': {
                    padding: '0 14px !important',
                    fontSize: '14px',
                    lineHeight: '20px',
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    gap: '8px',
                    alignItems: 'flex-start',
                    paddingTop: '8px !important',
                    paddingBottom: '8px !important',
                    flexWrap: 'wrap',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    lineHeight: '20px',
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Box
                    key={`tag-${option.id}-${index}`}
                    {...tagProps}
                    sx={{
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      borderRadius: '6px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: '#E5E7EB',
                      height: '20px',
                      pt: '2px',
                      pr: '4px',
                      pb: '2px',
                      pl: '9px',
                      gap: '3px',
                      margin: '2px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {option.name}
                    <Box
                      component="span"
                      onClick={() => {
                        const newValues = value.filter((_, i) => i !== index);
                        const chosenIds = newValues.map((v) => v.id);
                        const current = (newRepo.repoBuckets || []).filter((u) =>
                          chosenIds.includes((u.bucketId ?? '') as string)
                        );
                        setNewRepo((p) => ({ ...p, repoBuckets: current }));
                      }}
                      sx={{
                        ml: 0.5,
                        cursor: 'pointer',
                        fontSize: '14px',
                        '&:hover': { color: '#EF4444' },
                      }}
                    >
                      ×
                    </Box>
                  </Box>
                );
              })
            }
            onChange={(_, values) => {
              const chosenIds = values.map((v) => v.id);
              const current = (newRepo.repoBuckets || []).filter((u) =>
                chosenIds.includes((u.bucketId ?? '') as string)
              );
              const toAdd = values
                .filter((v) => !current.find((u) => u.bucketId === v.id))
                .map((v) => ({ bucketId: v.id, folder: '', master: false }));
              setNewRepo((p) => ({ ...p, repoUnits: [...current, ...toAdd] }));
            }}
            value={
              (newRepo.repoBuckets || [])
                .map((ru) => availableUnits.find((u) => u.id === ru.bucketId))
                .filter(Boolean) as ApiTypes.StorageUnit[]
            }
            sx={{
              width: '100%',
              maxWidth: '712px',
              mb: 3,
            }}
          />
        </Box>

        {(newRepo.repoBuckets || []).map((unit, idx) => {
          const unitInfo = availableUnits.find((u) => u.id === unit.bucketId);
          return (
            <Box
              key={`bucket-${unit.bucketId}-${idx}`}
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                maxWidth: '712px',
                height: '65px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #E9EAEB',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#181D27' }}>
                  Bucket name
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                  {unitInfo?.name || unit.bucketId}
                </Typography>
              </Box>
              <Box>
                <TextField
                  placeholder="Folder path (leave empty for default ID)"
                  value={unit.folder || ''}
                  onChange={(e) =>
                    setNewRepo((p) => ({
                      ...p,
                      repoBuckets: (p.repoBuckets || []).map((u, i) =>
                        i === idx ? { ...u, folder: e.target.value } : u
                      ),
                    }))
                  }
                  sx={{
                    width: '360px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#E9EAEB',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                    },
                  }}
                />
              </Box>
              <IconButton onClick={() => removeRepoUnit(idx)} sx={{ color: '#EF4444', p: 1 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}

        <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            onClick={() => navigate('/repositories/inventory')}
            sx={{
              backgroundColor: 'transparent',
              color: '#6B7280',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              padding: '8px 0',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#374151',
              },
            }}
          >
            Cancel
          </Button>
          {isEditMode ? (
            <Button
              onClick={handleUpdateRepo}
              disabled={loading || !isStepValid(1)}
              sx={{
                backgroundColor: '#617BFF',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#5169E0',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#9CA3AF',
                },
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          ) : (
            <Button
              onClick={() => {
                if (!isStepValid(1)) return;
                setCurrentStep(2);
              }}
              disabled={!isStepValid(1)}
              sx={{
                backgroundColor: '#617BFF',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#5169E0',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#9CA3AF',
                },
              }}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: '20px' }}
              >
                <span style={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>Next</span>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 12h12"
                    stroke="#BDC8FF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6l6 6-6 6"
                    stroke="#BDC8FF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Button>
          )}
        </Box>
      </Box>

      {/* Divider between sections */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
        <Divider sx={{ width: '1080px', borderColor: '#E5E7EB' }} />
      </Box>

      {/* Section: Link AI LLMs */}
      <Box
        sx={{
          mb: 4,
          position: 'relative',
          ...(!isEditMode && currentStep < 1
            ? { opacity: 0.5, pointerEvents: 'none' as const }
            : {}),
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            color: '#181D27',
            lineHeight: '26px',
            mb: 1,
          }}
        >
          Link AI LLMs
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            color: '#535862',
            lineHeight: '20px',
            mb: 3,
          }}
        >
          Select and link AI Language Model configurations (optional)
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#181D27',
              mb: 1,
            }}
          >
            AI Type
          </Typography>
          <FormControl
            sx={{
              width: '100%',
              maxWidth: '712px',
              mb: 3,
              '& .MuiOutlinedInput-root': {
                height: '44px',
                borderRadius: '8px',
                '& fieldset': {
                  borderWidth: '1px',
                  borderColor: '#E9EAEB',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#617BFF',
                },
              },
              '& .MuiSelect-select': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          >
            <Select
              value="OPENAI"
              displayEmpty
              disabled
              renderValue={() => 'OpenAI'}
            >
              <MenuItem value="OPENAI">OpenAI</MenuItem>
              <MenuItem value="ANTHROPIC">Anthropic</MenuItem>
              <MenuItem value="GOOGLE">Google</MenuItem>
              <MenuItem value="BEDROCK">AWS Bedrock</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#181D27',
              mb: 1,
            }}
          >
            Select AI LLMs
          </Typography>
          <Autocomplete
            multiple
            options={availableAiLlms.filter((opt) => {
              if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') return true;
              return opt.workspaceId === selectedWorkspaceId;
            })}
            getOptionLabel={(opt) => `${opt.name} (${opt.aiType})`}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Start typing..."
                sx={{
                  width: '712px',
                  '& .MuiOutlinedInput-root': {
                    minHeight: '44px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#E9EAEB',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D1D5DB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#617BFF',
                    },
                  },
                  '& .MuiAutocomplete-input': {
                    padding: '0 14px !important',
                    fontSize: '14px',
                    lineHeight: '20px',
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    gap: '8px',
                    alignItems: 'flex-start',
                    paddingTop: '8px !important',
                    paddingBottom: '8px !important',
                    flexWrap: 'wrap',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    lineHeight: '20px',
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Box
                    key={`tag-${option.id}-${index}`}
                    {...tagProps}
                    sx={{
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      borderRadius: '6px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: '#E5E7EB',
                      height: '20px',
                      pt: '2px',
                      pr: '4px',
                      pb: '2px',
                      pl: '9px',
                      gap: '3px',
                      margin: '2px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {option.name}
                    <Box
                      component="span"
                      onClick={() => {
                        const newValues = value.filter((_, i) => i !== index);
                        const chosenIds = newValues.map((v) => v.id);
                        const current = (newRepo.repoAiLlms || []).filter((u) =>
                          chosenIds.includes((u.aiLlmId ?? '') as string)
                        );
                        setNewRepo((p) => ({ ...p, repoAiLlms: current }));
                      }}
                      sx={{
                        ml: 0.5,
                        cursor: 'pointer',
                        fontSize: '14px',
                        '&:hover': { color: '#EF4444' },
                      }}
                    >
                      ×
                    </Box>
                  </Box>
                );
              })
            }
            onChange={(_, values) => {
              const chosenIds = values.map((v) => v.id);
              const current = (newRepo.repoAiLlms || []).filter((u) =>
                chosenIds.includes((u.aiLlmId ?? '') as string)
              );
              const toAdd = values
                .filter((v) => !current.find((u) => u.aiLlmId === v.id))
                .map((v) => ({ aiLlmId: v.id }));
              setNewRepo((p) => ({ ...p, repoAiLlms: [...current, ...toAdd] }));
            }}
            value={
              (newRepo.repoAiLlms || [])
                .map((ru) => availableAiLlms.find((u) => u.id === ru.aiLlmId))
                .filter(Boolean) as AiLlmTypes.AiLlmDTO[]
            }
            sx={{
              width: '100%',
              maxWidth: '712px',
              mb: 3,
            }}
          />
        </Box>

        {(newRepo.repoAiLlms || []).map((aiLlm, idx) => {
          const aiLlmInfo = availableAiLlms.find((a) => a.id === aiLlm.aiLlmId);
          return (
            <Box
              key={`ai-llm-${aiLlm.aiLlmId}-${idx}`}
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                maxWidth: '712px',
                height: '65px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #E9EAEB',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#181D27' }}>
                  AI LLM Configuration
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                  {aiLlmInfo?.name || aiLlm.aiLlmId} {aiLlmInfo && `(${aiLlmInfo.aiType})`}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setNewRepo((p) => ({
                    ...p,
                    repoAiLlms: (p.repoAiLlms || []).filter((_, i) => i !== idx),
                  }));
                }}
                sx={{ color: '#EF4444', p: 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}

        <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            onClick={() => navigate('/repositories/inventory')}
            sx={{
              backgroundColor: 'transparent',
              color: '#6B7280',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              padding: '8px 0',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#374151',
              },
            }}
          >
            Cancel
          </Button>
          {isEditMode ? (
            <Button
              onClick={handleUpdateRepo}
              disabled={loading || !isStepValid(1)}
              sx={{
                backgroundColor: '#617BFF',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#5169E0',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#9CA3AF',
                },
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          ) : (
            <Button
              onClick={() => {
                if (!isStepValid(1)) return;
                setCurrentStep(2);
              }}
              disabled={!isStepValid(1)}
              sx={{
                backgroundColor: '#617BFF',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#5169E0',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#9CA3AF',
                },
              }}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: '20px' }}
              >
                <span style={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>Next</span>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 12h12"
                    stroke="#BDC8FF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6l6 6-6 6"
                    stroke="#BDC8FF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Button>
          )}
        </Box>
      </Box>

      {/* Only show Finish setup section in create mode, not in edit mode */}
      {!isEditMode && (
        <>
          {/* Divider between sections */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
            <Divider sx={{ width: '1080px', borderColor: '#E5E7EB' }} />
          </Box>

          {/* Section: Finish setup (API Keys section removed; keep only final action) */}
          <Box
            sx={{
              mb: 4,
              position: 'relative',
              ...(currentStep < 2 ? { opacity: 0.5, pointerEvents: 'none' as const } : {}),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Button
                onClick={handleCreateRepo}
                disabled={loading || !(isStepValid(0) && isStepValid(1))}
                data-tutorial="repo-submit"
                sx={{
                  backgroundColor: '#617BFF',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#5169E0',
                  },
                  '&:disabled': {
                    backgroundColor: '#D1D5DB',
                    color: '#9CA3AF',
                  },
                }}
              >
                {loading ? 'Creating…' : 'Finish setup'}
              </Button>
            </Box>

            {/* Info bubbles: API keys + SDK supported */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxWidth: '712px' }}>
              {/* API keys info */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  p: 2,
                  backgroundColor: '#F0F9FF',
                  borderRadius: '8px',
                  border: '1px solid #BAE6FD',
                  flex: 1,
                  minWidth: '280px',
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20, color: '#0284C7', flexShrink: 0, mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#0C4A6E', mb: 0.5 }}>
                    API keys
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#075985', lineHeight: 1.5 }}>
                    After creating your repository, you can manage API keys, AI, storage, and other resources in the API keys page (repository settings).
                  </Typography>
                </Box>
              </Box>
              {/* SDK supported info */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  p: 2,
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  flex: 1,
                  minWidth: '280px',
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20, color: '#6B7280', flexShrink: 0, mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>
                    Supported SDKs
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: newRepo.storageType === ApiTypes.StorageType.S3 ? '#FF9900' : '#9CA3AF' }}>S3</span>
                    <span style={{ fontSize: '12px', color: '#D1D5DB' }}>•</span>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: newRepo.storageType === ApiTypes.StorageType.GCS ? '#4285F4' : '#9CA3AF' }}>GCS</span>
                    <span style={{ fontSize: '12px', color: '#D1D5DB' }}>•</span>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: newRepo.storageType === ApiTypes.StorageType.BLOB ? '#0078D4' : '#9CA3AF' }}>Blob</span>
                    <span style={{ fontSize: '12px', color: '#D1D5DB' }}>•</span>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#10A37F' }}>OpenAI</span>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AddRepo;
